/**
 * User Roles Hook Module
 * A custom React hook for managing user role operations
 * Provides CRUD operations for user roles using React Query
 */

import dynamicAPI from "./dynamic-API";
import { useQuery, useMutation, useQueryClient } from "react-query";

/**
 * Base API endpoint for user role operations
 */
const url = "/api/auth/user-roles";

/**
 * Query key for React Query caching
 */
const queryKey = "userRoles";

/**
 * Custom hook for managing user roles
 * Handles fetching, creating, updating and deleting user roles
 *
 * @param {Object} props - Hook properties
 * @param {number} [props.page=1] - Current page number for pagination
 * @param {string} [props.q=""] - Search query string
 * @param {number} [props.limit=25] - Number of items per page
 * @returns {Object} Object containing query and mutation functions for user roles
 */
const useUserRolesHook = (props) => {
  const { page = 1, q = "", limit = 25 } = props;
  const queryClient = useQueryClient();

  /**
   * Query hook for fetching user roles
   * Retrieves paginated list of user roles with optional search
   */
  const getUserRoles = useQuery(
    queryKey,
    async () =>
      await dynamicAPI("get", `${url}?page=${page}&q=${q}&limit=${limit}`, {}),
    { retry: 0 },
  );

  /**
   * Mutation hook for posting user role by ID
   * Creates a new user role entry with specific ID
   */
  const postUserRoleById = useMutation(
    async (id) => await dynamicAPI("post", `${url}/${id}`, {}),
    { retry: 0 },
  );

  /**
   * Mutation hook for updating user role
   * Updates existing user role and invalidates query cache
   */
  const updateUserRole = useMutation(
    async (obj) => await dynamicAPI("put", `${url}/${obj._id}`, obj),
    {
      retry: 0,
      onSuccess: () => queryClient.invalidateQueries([queryKey]),
    },
  );

  /**
   * Mutation hook for deleting user role
   * Removes user role and invalidates query cache
   */
  const deleteUserRole = useMutation(
    async (id) => await dynamicAPI("delete", `${url}/${id}`, {}),
    {
      retry: 0,
      onSuccess: () => queryClient.invalidateQueries([queryKey]),
    },
  );

  /**
   * Mutation hook for creating new user role
   * Creates new user role entry and invalidates query cache
   */
  const postUserRole = useMutation(
    async (obj) => await dynamicAPI("post", url, obj),
    {
      retry: 0,
      onSuccess: () => queryClient.invalidateQueries([queryKey]),
    },
  );

  return {
    getUserRoles,
    updateUserRole,
    deleteUserRole,
    postUserRole,
    postUserRoleById,
  };
};

export default useUserRolesHook;
